/**
 * Preferências do loop (período, passo, velocidade, modo, qualidade) e estado
 * da reprodução.
 */
import { create } from 'zustand';
import type { QualityMode } from '../goes/budget';
import type { TimeRange } from '../state/time';
import type { Period, StepMinutes } from './frames';

export type LoopMode = 'loop' | 'bounce';

export interface LoopSettings {
  period: Period;
  /** Início/fim (epoch ms UTC) do período personalizado. */
  custom: TimeRange | null;
  step: StepMinutes;
  /** Quadros por segundo. */
  fps: number;
  mode: LoopMode;
  /** Pausa extra no último quadro (ms). */
  dwellMs: number;
  quality: QualityMode;
}

export const DEFAULT_LOOP_SETTINGS: LoopSettings = {
  period: '3h',
  custom: null,
  step: 10,
  fps: 3,
  mode: 'loop',
  dwellMs: 1000,
  quality: 'auto',
};

export const FPS_MIN = 1;
export const FPS_MAX = 15;
export const DWELL_OPTIONS_MS = [0, 500, 1000, 2000, 3000];

interface LoopState extends LoopSettings {
  playing: boolean;
  /** Reproduzindo, mas esperando o próximo quadro carregar. */
  buffering: boolean;
  /** Instante pedido pelo link compartilhado, aplicado quando os quadros chegarem. */
  pendingFrameTime: number | null;
  /** Exportação em andamento: controles travados e quadros congelados (ver playback.setExportLock). */
  exportLocked: boolean;
  /**
   * O loop inteiro já foi liberado para baixar (o usuário apertou play nesta
   * visita). Antes disso o portal fica parado na medição mais recente e baixa
   * só o quadro na tela — e os que o usuário pedir navegando (decisão de
   * 23/09/2026, a pedido do usuário; ver docs/app-core.md, "Abre parado").
   */
  loopArmed: boolean;
  setSettings: (patch: Partial<LoopSettings>) => void;
}

export const useLoopStore = create<LoopState>()((set) => ({
  ...DEFAULT_LOOP_SETTINGS,
  playing: false,
  buffering: false,
  pendingFrameTime: null,
  exportLocked: false,
  loopArmed: false,
  setSettings: (patch) => set(patch),
}));

/**
 * Pode baixar o loop inteiro? Sim depois do primeiro play e durante a
 * exportação (o vídeo precisa de todos os quadros). Senão, só o quadro atual
 * e os que já foram baixados (voltar a eles não custa rede).
 */
export function loopLoadAllowed(s: Pick<LoopState, 'loopArmed' | 'exportLocked'> = useLoopStore.getState()): boolean {
  return s.loopArmed || s.exportLocked;
}
